import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, PlusSquare, CalendarCheck, Heart,
  Users, ShieldCheck, LogOut, Home as HomeIcon, Bell, Loader2,
  Megaphone, Activity, ClipboardCheck, UserCheck, MapPinned,
  BarChart3, BadgeCheck, Layers,
  Menu, X, Map as MapIcon, PhoneCall, HelpCircle, MessageSquareText, Search,
  ChevronDown, ChevronRight
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";
import { toast } from "sonner";
import { PHASE1_ENABLED } from "@/lib/featureFlags";

const ROLE_LABELS = {
  buyer: "Buyer",
  owner: "Owner",
  agent: "Agent",
  office: "Office User",
  admin: "Admin",
  super_admin: "Super Admin",
};

export default function DashboardLayout() {
  const { user, logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    setSidebarOpen(false);
    const result = await logout();
    if (!result.ok) {
      toast.error(result.error || "Could not sign out. Please try again.");
      return;
    }
    navigate("/", { replace: true });
    toast.success("Logged out successfully");
  };
  const [addPropExpanded, setAddPropExpanded] = useState(() => {
    return location.pathname.includes("/dashboard/property/new") || location.pathname.includes("/dashboard/properties/new");
  });
  const [userMgmtExpanded, setUserMgmtExpanded] = useState(() => {
    return location.pathname.includes("/dashboard/admin/users") ||
           location.pathname.includes("/dashboard/admin/agents") ||
           location.pathname.includes("/dashboard/admin/offices");
  });
  const [propertyMgmtExpanded, setPropertyMgmtExpanded] = useState(() => {
    return location.pathname.includes("/dashboard/admin/properties") ||
           location.pathname === "/dashboard/admin/plots";
  });
  const [siteVisitsExpanded, setSiteVisitsExpanded] = useState(() =>
    location.pathname.startsWith("/dashboard/admin/site-visits")
  );
  const [plotDemandExpanded, setPlotDemandExpanded] = useState(() =>
    location.pathname.startsWith("/dashboard/admin/plots-on-demand")
  );
  const [leadMgmtExpanded, setLeadMgmtExpanded] = useState(() => {
    return location.pathname === "/dashboard/leads" ||
           location.pathname === "/dashboard/admin/leads" ||
           location.pathname === "/dashboard/admin/lead-explorer";
  });
  // Cross-entity deep links can move the user into a nested admin page without
  // clicking its sidebar group first. Keep the relevant group visibly open.
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/dashboard/property/new") || path.includes("/dashboard/properties/new")) setAddPropExpanded(true);
    if (path.includes("/dashboard/admin/users") || path.includes("/dashboard/admin/agents") || path.includes("/dashboard/admin/offices")) setUserMgmtExpanded(true);
    if (path.includes("/dashboard/admin/properties") || path === "/dashboard/admin/plots") setPropertyMgmtExpanded(true);
    if (path.startsWith("/dashboard/admin/site-visits")) setSiteVisitsExpanded(true);
    if (path.startsWith("/dashboard/admin/plots-on-demand")) setPlotDemandExpanded(true);
    if (path === "/dashboard/leads" || path === "/dashboard/admin/leads" || path === "/dashboard/admin/lead-explorer") setLeadMgmtExpanded(true);
  }, [location.pathname]);
  const queryParams = new URLSearchParams(location.search);
  const isSubActive = (type) => {
    const isPathMatch = location.pathname.includes("/dashboard/property/new") || location.pathname.includes("/dashboard/properties/new");
    return isPathMatch && queryParams.get("type") === type;
  };
  const role = String(user?.role || "buyer").toLowerCase().replace("office_user", "office");
  const darkShell = ["agent", "admin", "super_admin"].includes(role);

  // Keep the intentional role theme split while using one layout component.
  // Applying the class at the document root also themes Radix dialog portals;
  // cleanup restores any class that existed before this dashboard mounted.
  useEffect(() => {
    const root = document.documentElement;
    const previouslyDark = root.classList.contains("dark");
    root.classList.toggle("dark", darkShell);
    return () => root.classList.toggle("dark", previouslyDark);
  }, [darkShell]);

  if (!user) return null;

  const sections = buildSections(role);
  // Every role shares this responsive shell, spacing and interaction system;
  // only the role-approved color tokens differ.
  return (
    <div className={`${darkShell ? "dark" : ""} premium-dashboard-shell min-h-screen bg-secondary flex`} data-testid="dashboard-layout" data-role-theme={darkShell ? "dark" : "light"}>
      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close dashboard navigation"
        />
      )}

      <aside aria-label="Dashboard navigation" className={`premium-sidebar fixed inset-y-0 left-0 z-50 md:sticky md:top-0 md:h-screen md:z-20 w-72 shrink-0 bg-primary text-primary-foreground flex-col p-5 sm:p-6 overflow-y-auto scrollbar-none transition-transform duration-200 ease-out md:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      } md:flex`}>
        <div className="flex justify-between items-center mb-8">
          <Link to="/" data-testid="dashboard-brand" aria-label="ShreeHomes home">
            {/* Shared logo keeps every role portal visually aligned. */}
            <BrandLogo inverse size="md" subtitle="Verified land portal" />
          </Link>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="md:hidden text-primary-foreground/80 hover:text-primary-foreground focus:outline-none"
            aria-label="Close Sidebar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6 p-4 rounded-2xl bg-primary-foreground/5 border border-primary-foreground/10">
          <p className="text-[10px] font-bold uppercase tracking-widest text-primary-foreground/50">Signed in as</p>
          <p className="font-display font-bold text-sm mt-1 truncate">{user.name || user.email || "User"}</p>
          <p className="text-[11px] text-primary-foreground/70 capitalize mt-1 flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
            {ROLE_LABELS[role] || role}{user.approved === false ? " · pending approval" : ""}
          </p>
        </div>

        <nav className="flex-1 space-y-5" data-testid="dashboard-nav">
          {sections.map((sec) => (
            <div key={sec.label || "main"}>
              {sec.label && <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary-foreground/40 mb-1.5 px-3">{sec.label}</p>}
              <div className="space-y-0.5">
                {sec.items.map((it) => {
                  if ((role === "admin" || role === "super_admin") && (it.label === "Site Visits" || it.label === "Plots on Demand")) {
                    const siteVisits = it.label === "Site Visits";
                    return <AdminWorkflowNavGroup
                      key={it.label}
                      item={it}
                      expanded={siteVisits ? siteVisitsExpanded : plotDemandExpanded}
                      onToggle={() => siteVisits ? setSiteVisitsExpanded(value => !value) : setPlotDemandExpanded(value => !value)}
                      onNavigate={() => setSidebarOpen(false)}
                      id={siteVisits ? "dashboard-site-visits-links" : "dashboard-plots-on-demand-links"}
                      testId={siteVisits ? "dash-nav-site-visits-toggle" : "dash-nav-plots-on-demand-toggle"}
                    />;
                  }
                  if (it.to === "/dashboard/property/new") {
                    return (
                      <div key={it.label} className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => setAddPropExpanded(!addPropExpanded)}
                          aria-expanded={addPropExpanded}
                          aria-controls="dashboard-add-property-links"
                          className="flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] font-semibold text-primary-foreground/70 hover:bg-primary-foreground/5 hover:text-primary-foreground w-full text-left"
                          data-testid="dash-nav-add-property-toggle"
                        >
                          <div className="flex items-center gap-3">
                            <it.icon className="w-4 h-4 shrink-0" />
                            <span>{it.label}</span>
                          </div>
                          {addPropExpanded ? <ChevronDown className="h-4 w-4 opacity-75" aria-hidden="true"/> : <ChevronRight className="h-4 w-4 opacity-75" aria-hidden="true"/>}
                        </button>
                        {addPropExpanded && (
                          <div id="dashboard-add-property-links" className="pl-4 flex flex-col space-y-0.5 ml-4 mt-1 mb-2 border-l border-primary-foreground/10">
                            <NavLink
                              to="/dashboard/property/new?type=land"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-land"
                              className={
                                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  isSubActive("land") ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <MapIcon className="w-3.5 h-3.5" />
                              <span>Plot / Land</span>
                            </NavLink>
                            <NavLink
                              to="/dashboard/property/new?type=house"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-house"
                              className={
                                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  isSubActive("house") ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <HomeIcon className="w-3.5 h-3.5" />
                              <span>House / Villa</span>
                            </NavLink>
                            <NavLink
                              to="/dashboard/property/new?type=flat"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-flat"
                              className={
                                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  isSubActive("flat") ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <Building2 className="w-3.5 h-3.5" />
                              <span>Apartment / Flat</span>
                            </NavLink>
                          </div>
                        )}
                      </div>
                    );
                  }
                  if ((role === "admin" || role === "super_admin") && it.label === "Lead Management") {
                    return (
                      <div key={it.label} className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => setLeadMgmtExpanded(!leadMgmtExpanded)}
                          aria-expanded={leadMgmtExpanded}
                          aria-controls="dashboard-lead-management-links"
                          className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-[13px] font-semibold text-primary-foreground/70 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                          data-testid="dash-nav-lead-management-toggle"
                          title="Open Lead Management navigation"
                        >
                          <div className="flex items-center gap-3">
                            <it.icon className="h-4 w-4 shrink-0" />
                            <span>{it.label}</span>
                          </div>
                          {leadMgmtExpanded ? <ChevronDown className="h-4 w-4 opacity-75" aria-hidden="true"/> : <ChevronRight className="h-4 w-4 opacity-75" aria-hidden="true"/>}
                        </button>
                        {leadMgmtExpanded && (
                          <div id="dashboard-lead-management-links" className="ml-4 mt-1 mb-2 flex flex-col space-y-0.5 border-l border-primary-foreground/10 pl-4">
                            <NavLink
                              to="/dashboard/admin/leads"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-lead-kanban"
                              title="Open the existing Lead Management Kanban board"
                              className={({ isActive }) =>
                                `flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <ClipboardCheck className="h-3.5 w-3.5" />
                              <span>Kanban Board</span>
                            </NavLink>
                            {!PHASE1_ENABLED && <NavLink
                              to="/dashboard/admin/lead-explorer"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-lead-explorer"
                              title="Search property, buyer, agent, and office lead relationships"
                              className={({ isActive }) =>
                                `flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <Search className="h-3.5 w-3.5" />
                              <span>Lead Explorer</span>
                            </NavLink>}
                          </div>
                        )}
                      </div>
                    );
                  }
                  if (it.label === "Users & Offices") {
                    return (
                      <div key={it.label} className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => setUserMgmtExpanded(!userMgmtExpanded)}
                          aria-expanded={userMgmtExpanded}
                          aria-controls="dashboard-users-offices-links"
                          className="flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] font-semibold text-primary-foreground/70 hover:bg-primary-foreground/5 hover:text-primary-foreground w-full text-left"
                          data-testid="dash-nav-users-offices-toggle"
                        >
                          <div className="flex items-center gap-3">
                            <it.icon className="w-4 h-4 shrink-0" />
                            <span>{it.label}</span>
                          </div>
                          {userMgmtExpanded ? <ChevronDown className="h-4 w-4 opacity-75" aria-hidden="true"/> : <ChevronRight className="h-4 w-4 opacity-75" aria-hidden="true"/>}
                        </button>
                        {userMgmtExpanded && (
                          <div id="dashboard-users-offices-links" className="pl-4 flex flex-col space-y-0.5 ml-4 mt-1 mb-2 border-l border-primary-foreground/10">
                            <NavLink
                              to="/dashboard/admin/users"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-users"
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <Users className="w-3.5 h-3.5" />
                              <span>Users</span>
                            </NavLink>
                            <NavLink
                              to="/dashboard/admin/agents"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-agents"
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <BadgeCheck className="w-3.5 h-3.5" />
                              <span>Agents</span>
                            </NavLink>
                            <NavLink
                              to="/dashboard/admin/offices"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-offices"
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <Building2 className="w-3.5 h-3.5" />
                              <span>Offices</span>
                            </NavLink>
                          </div>
                        )}
                      </div>
                    );
                  }
                  if (it.label === "Properties & Listings") {
                    return (
                      <div key={it.label} className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => setPropertyMgmtExpanded(!propertyMgmtExpanded)}
                          aria-expanded={propertyMgmtExpanded}
                          aria-controls="dashboard-properties-listing-links"
                          className="flex items-center justify-between px-3 py-2.5 rounded-xl text-[13px] font-semibold text-primary-foreground/70 hover:bg-primary-foreground/5 hover:text-primary-foreground w-full text-left"
                          data-testid="dash-nav-properties-listings-toggle"
                        >
                          <div className="flex items-center gap-3">
                            <it.icon className="w-4 h-4 shrink-0" />
                            <span>{it.label}</span>
                          </div>
                          {propertyMgmtExpanded ? <ChevronDown className="h-4 w-4 opacity-75" aria-hidden="true"/> : <ChevronRight className="h-4 w-4 opacity-75" aria-hidden="true"/>}
                        </button>
                        {propertyMgmtExpanded && (
                          <div id="dashboard-properties-listing-links" className="pl-4 flex flex-col space-y-0.5 ml-4 mt-1 mb-2 border-l border-primary-foreground/10">
                            <NavLink
                              to="/dashboard/admin/properties"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-properties"
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <Building2 className="w-3.5 h-3.5" />
                              <span>Properties</span>
                            </NavLink>
                            <NavLink
                              to="/dashboard/admin/plots"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-plots"
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <Layers className="w-3.5 h-3.5" />
                              <span>Listed Plots & Land</span>
                            </NavLink>
                            <NavLink
                              to="/dashboard/trending"
                              onClick={() => setSidebarOpen(false)}
                              data-testid="dash-nav-sub-trending"
                              className={({ isActive }) =>
                                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                                  isActive ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"
                                }`
                              }
                            >
                              <Activity className="w-3.5 h-3.5" />
                              <span>Trending Leaderboard</span>
                            </NavLink>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.end}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`dash-nav-${it.label.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}
                      className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold transition ${isActive ? "bg-primary-foreground/15 text-primary-foreground" : "text-primary-foreground/70 hover:bg-primary-foreground/5 hover:text-primary-foreground"}`}
                    >
                      <it.icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{it.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-primary-foreground/10 pt-4 mt-4 space-y-1">
          <Button variant="ghost" onClick={() => { setSidebarOpen(false); navigate("/dashboard/profile"); }} className="w-full justify-start text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10" data-testid="dash-home-btn">
            <UserCheck className="w-4 h-4 mr-2"/> My profile
          </Button>
          <Button
            variant="ghost"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="w-full justify-start text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10 disabled:opacity-60"
            data-testid="dash-logout-btn"
          >
            {isLoggingOut ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin text-[#10b981]" /> Signing out...
              </>
            ) : (
              <>
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </>
            )}
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="dashboard-mobile-header md:hidden flex items-center justify-between bg-primary text-primary-foreground p-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="text-primary-foreground/80 hover:text-primary-foreground p-1 rounded-md"
              aria-label="Open Sidebar"
            >
              <Menu className="w-6 h-6" />
            </button>
            <Link to="/" aria-label="ShreeHomes home">
              <BrandLogo inverse compact size="sm" />
            </Link>
          </div>
          <span className="text-xs uppercase font-bold tracking-widest">{ROLE_LABELS[role] || role}</span>
        </header>
        <main className="dashboard-content-surface p-4 sm:p-6 lg:p-8 xl:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function AdminWorkflowNavGroup({ item, expanded, onToggle, onNavigate, id, testId }) {
  return <div className="flex flex-col">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={id}
      className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-[13px] font-semibold text-primary-foreground/70 transition-colors duration-200 ease-out hover:bg-primary-foreground/5 hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/50"
      data-testid={testId}
      title={`Open ${item.label} navigation`}
    >
      <span className="flex items-center gap-3"><item.icon className="h-4 w-4 shrink-0"/><span>{item.label}</span></span>
      {expanded ? <ChevronDown className="h-4 w-4 opacity-75" aria-hidden="true"/> : <ChevronRight className="h-4 w-4 opacity-75" aria-hidden="true"/>}
    </button>
    <div className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${expanded ? "grid-rows-[1fr] opacity-100" : "pointer-events-none grid-rows-[0fr] opacity-0"}`} aria-hidden={!expanded}>
      <div className="overflow-hidden"><div id={id} className="ml-4 mt-1 mb-2 flex flex-col space-y-0.5 border-l border-primary-foreground/10 pl-4">
        {(item.children || []).map(child => <NavLink
          key={child.to}
          to={child.to}
          end={child.end}
          onClick={onNavigate}
          tabIndex={expanded ? 0 : -1}
          title={child.title}
          data-testid={child.testId}
          className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ease-out ${isActive ? "bg-primary-foreground/20 text-primary-foreground" : "text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground"}`}
        ><child.icon className="h-3.5 w-3.5"/><span>{child.label}</span></NavLink>)}
      </div></div>
    </div>
  </div>;
}

function buildSections(role) {
  const sections = [{ label: null, items: [{ to: "/", label: "Home", icon: HomeIcon }, { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true }] }];

  if (role === "buyer") {
    sections.push({ label: "Buyer", items: [
      { to: "/dashboard/property/new", label: "Add Property", icon: PlusSquare },
      { to: "/dashboard/saved", label: "Saved Properties", icon: Heart },
      { to: "/dashboard/appointments", label: "Site Visits", icon: CalendarCheck },
      { to: "/dashboard/plot-demands", label: "Plots on Demand", icon: MapPinned },
      { to: "/dashboard/enquiries", label: "My Enquiries", icon: PhoneCall },
      { to: "/dashboard/notifications", label: "Notifications", icon: Bell },

    ]});
    return sections;
  }

  if (role === "owner") {
    sections.push({ label: "Owner Listings", items: [
      { to: "/dashboard/properties", label: "My Properties", icon: Building2 },
      { to: "/dashboard/property/new", label: "Add Property", icon: PlusSquare },
      { to: "/dashboard/appointments", label: "Site Visits", icon: CalendarCheck },
      { to: "/dashboard/saved", label: "Saved Properties", icon: Heart },
      { to: "/dashboard/plot-demands", label: "Plots on Demand", icon: MapPinned },
      { to: "/dashboard/enquiries", label: "My Enquiries", icon: PhoneCall },
      { to: "/dashboard/notifications", label: "Notifications", icon: Bell },

    ]});
    return sections;
  }

  if (role === "agent") {
    sections.push({ label: "Agent Listings", items: [
      { to: "/dashboard/properties", label: "My Properties", icon: Building2 },
      { to: "/dashboard/property/new", label: "Add Property", icon: PlusSquare },
      { to: "/dashboard/leads", label: "Leads", icon: Megaphone },
      { to: "/dashboard/agent/visits", label: "Assigned Site Visits", icon: CalendarCheck },
      { to: "/dashboard/notifications", label: "Notifications", icon: Bell },
    ]});
    return sections;
  }

  if (role === "office") {
    sections.push({ label: "Verification", items: [
      { to: "/dashboard/office/verification", label: "Office Verification Queue", icon: ClipboardCheck },
      { to: "/dashboard/properties", label: "Office Properties", icon: Building2 },
      { to: "/dashboard/property/new", label: "Add Property", icon: PlusSquare },
      { to: "/dashboard/office/site-visits", label: "Site Visit Queue", icon: CalendarCheck },
      { to: "/dashboard/office/crm-leads", label: "CRM Dashboard", icon: BarChart3 },
      { to: "/dashboard/leads", label: "Lead Management", icon: ClipboardCheck },
    ]});
    sections.push({ label: "Updates", items: [
      { to: "/dashboard/notifications", label: "Notifications", icon: Bell },
    ]});
    return sections;
  }

  if (role === "admin" || role === "super_admin") {
    const adminItems = [
      { to: "/", label: "Home", icon: HomeIcon },
      { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/dashboard/admin/approvals", label: "Final Verification", icon: ShieldCheck },
      { to: "/dashboard/admin/operations", label: "Operations Hub", icon: Activity },
      { to: "/dashboard/admin/lead-assignments", label: "Lead Assignment", icon: UserCheck },
      { to: "/dashboard/admin/leads", label: "Lead Management", icon: ClipboardCheck },
      { to: "/dashboard/admin/site-visits", label: "Site Visits", icon: CalendarCheck, children: [
        { to: "/dashboard/admin/site-visits", label: "Overview", icon: LayoutDashboard, end: true, title: "Open Site Visits overview", testId: "dash-nav-site-visits-overview" },
        { to: "/dashboard/admin/site-visits/workbench", label: "Workbench", icon: Search, title: "Search property and buyer Site Visit relationships", testId: "dash-nav-site-visits-workbench" },
        { to: "/dashboard/admin/site-visits/assignments", label: "Assignment & Management", icon: UserCheck, title: "Assign, reschedule and manage Site Visits", testId: "dash-nav-site-visits-assignments" },
      ] },
      { to: "/dashboard/admin/users", label: "Users & Offices", icon: Users },
      { to: "/dashboard/property/new", label: "Add Property", icon: PlusSquare },
      { to: "/dashboard/admin/properties", label: "Properties & Listings", icon: Building2, children: [
        { to: "/dashboard/admin/properties", label: "Explorer", icon: Search, end: true, title: "Property inventory explorer", testId: "dash-nav-properties-explorer" },
        { to: "/dashboard/property/new", label: "Add Property", icon: PlusSquare, title: "Add new property", testId: "dash-nav-add-property" },
      ] },
      { to: "/dashboard/admin/plots-on-demand", label: "Plots on Demand", icon: MapPinned, children: [
        { to: "/dashboard/admin/plots-on-demand", label: "Overview", icon: LayoutDashboard, end: true, title: "Open Plots on Demand overview", testId: "dash-nav-plots-on-demand-overview" },
        { to: "/dashboard/admin/plots-on-demand/workbench", label: "Workbench", icon: Search, title: "Search every Plots on Demand relationship", testId: "dash-nav-plots-on-demand-workbench" },
        { to: "/dashboard/admin/plots-on-demand/assignments", label: "Assignment & Management", icon: UserCheck, title: "Assign and manage Plots on Demand", testId: "dash-nav-plots-on-demand-assignments" },
        { to: "/dashboard/admin/plots-on-demand/manage", label: "Detailed Management", icon: Layers, title: "Open detailed Plots on Demand records and activity history", testId: "dash-nav-plots-on-demand-manage" },
      ] },
      { to: "/dashboard/admin/faqs", label: "FAQ CMS", icon: HelpCircle },
      { to: "/dashboard/admin/reviews", label: "Review Moderation", icon: MessageSquareText },
      { to: "/dashboard/office/crm-leads", label: "CRM Dashboard", icon: BarChart3 },
      { to: "/dashboard/admin/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/dashboard/notifications", label: "Notifications", icon: Bell },
    ];
    if (PHASE1_ENABLED) {
      return [{
        label: null,
        items: adminItems
            .filter((item) => ![
              "/dashboard/admin/operations",
              "/dashboard/admin/analytics",
            ].includes(item.to))
            .map((item) => {
              if (item.to === "/dashboard/admin/leads") {
                return {
                  ...item,
                  children: (item.children || []).filter((child) => child.to !== "/dashboard/admin/lead-explorer"),
                };
              }
              if (item.to === "/dashboard/admin/site-visits") {
                return {
                  ...item,
                  children: (item.children || []).filter((child) => child.to !== "/dashboard/admin/site-visits/workbench"),
                };
              }
              if (item.to === "/dashboard/admin/plots-on-demand") {
                return {
                  ...item,
                  children: (item.children || []).filter((child) => child.to !== "/dashboard/admin/plots-on-demand/workbench"),
                };
              }
              return item;
            }),
      }];
    }
    return [{ label: null, items: adminItems }];
  }

  sections.push({ label: "General", items: [
    { to: "/dashboard/appointments", label: "Appointments", icon: CalendarCheck },
    { to: "/dashboard/notifications", label: "Notifications", icon: Bell },
  ]});
  return sections;
}
